// @ts-nocheck

import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { MyMatchHelperTooltipLabelsByMarketId } from '../../constants/mymatch';
import { ON_MY_MATCH_TOGGLE_TRANSITION_DURATION } from '../../constants/ui/mymatch';
import useMatchBetFiltersContext from '../../hooks/useMatchBetFiltersContext';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import useMergeProps from '../../hooks/useMergeProps';
import { LineUpOpenViewButtonWrapperClassName } from '../../modules/line-up/components/LineUpOpenViewButton/LineUpOpenViewButton';
import { selectMatchViewMode } from '../../selectors/match.selector';
import { selectUserDisplayBetSliders } from '../../selectors/user/userPrimary.selector';
import theme from '../../styled-components/dark';
import { withOpacity } from '../../styled-components/dark/helpers';
import { AutoScrollAnchor } from '../../styled-components/shared';
import { PresentationModes } from '../../types/betGroup';
import { getPlayerIdFromFilterId } from '../../types/matchBetFilters';
import { MatchViewModes } from '../../types/matchView';
import { BetGroupLabelWithLineClassName } from '../BetGroupLabelWithLine';
import { ExpandButtonClassName } from '../ExpandButton';
import InfosLabel, { InfosLabelTypes } from '../InfosLabel/InfosLabel';
import MatchGroupHeader from '../MatchGroupHeader';
import MatchGroupStarterFilters from '../MatchGroupStarterFilters';
import MyMatchDynamicBackground, { MyMatchDynamicBackgroundRadialClassName } from '../MyMatchDynamicBackground';
import {
  PercentDistributionBackgroundBarClassName,
  PercentDistributionCompletenessClassName,
} from '../PercentDistribution';
import { PlayerWidgetButtonSvgClassName } from '../PlayerWidgetButton';
import { SliderRightBarClassName, SliderWrapperClassName } from '../Slider';
import { TabsSelectedBackgroundClassName, TabsWrapperClassName } from '../Tabs';

import BetGroupFactory from './BetGroupFactory';
import makeBetGroupFactoryPropsSelector from './BetGroupFactory.selector';

const Wrapper = styled.div`
  position: relative;
  ${({ $noPadding }) => ($noPadding ? '' : 'padding: 0.5em 0;')}
`;

const MatchGroupBody = styled.div`
  position: relative;
  border-radius: ${theme.Card.borderRadius};
  margin: 0 auto 0;
  animation: glow 1s ease-in-out infinite;
  transform: translateZ(0);
  transition: background-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
  --MyMatchDynamicBackground-defaultBackgroundColor: ${theme.MatchBetGroup.backgroundColor};

  &
    .${SliderRightBarClassName},
    .${BetGroupLabelWithLineClassName},
    .${ExpandButtonClassName},
    .${PlayerWidgetButtonSvgClassName},
    .${PercentDistributionBackgroundBarClassName},
    .${PercentDistributionCompletenessClassName},
    .${TabsWrapperClassName},
    .${TabsSelectedBackgroundClassName},
    .${LineUpOpenViewButtonWrapperClassName} {
    transition: none ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
    transition-property: background-color, color;
  }

  ${({ $myMatchViewModeIsAvailable }) =>
    $myMatchViewModeIsAvailable
      ? css`
          background-color: transparent;

          & .${SliderWrapperClassName} {
            --Slider-rightBarColor: rgba(255, 255, 255, 0.1);
          }

          & .${BetGroupLabelWithLineClassName} {
            background-color: rgba(255, 255, 255, 0.1);
          }

          & .${ExpandButtonClassName} {
            background-color: rgba(255, 255, 255, 0.08);
          }

          & .${PlayerWidgetButtonSvgClassName}, & .${PercentDistributionCompletenessClassName} {
            color: ${withOpacity('#ffffff', '88')};
          }

          & .${PercentDistributionBackgroundBarClassName} {
            background-color: rgba(255, 255, 255, 0.1);
          }

          & .${LineUpOpenViewButtonWrapperClassName} {
            background-color: rgba(0, 0, 0, 0.5);
          }

          --Tabs-defaultBackgroundColor: rgba(255, 255, 255, 0.1);
          --Tabs-defaultColor: rgba(255, 255, 255, 0.75);
          --Tabs-selectedBackgroundColor: rgba(255, 255, 255, 0.15);
          --Tabs-selectedColor: rgb(255, 255, 255);
        `
      : undefined}
`;

const StyledMyMatchDynamicBackground = styled(MyMatchDynamicBackground)`
  & .${MyMatchDynamicBackgroundRadialClassName} {
    left: 0%;
    top: -9.5em;
    width: 400%;
  }
`;

const StyledInfosLabel = styled(InfosLabel)`
  margin-top: 0;
  z-index: 1;
  margin: 0.4em;
`;

const getDefaultPresentationModeIndex = ({ displayBetSlidersByDefault, betPresentationModes }) => {
  if (displayBetSlidersByDefault) {
    const modeIndex = betPresentationModes.findIndex((mode) => {
      return [PresentationModes.SLIDER_BY_BET, PresentationModes.SLIDER_BY_OUTCOME].includes(mode.type);
    });

    if (modeIndex !== -1) {
      return modeIndex;
    }
  }
  return 0;
};

const BetGroupFactoryContainer = (ownProps) => {
  const { matchId: matchIdFromMatchView } = useMatchViewContext();
  const {
    matchId,
    betsData,
    title,
    helperText,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setShouldAllowCursorMode,
    showAlternativeTitle,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    withFirstBetAnchor,
    className,
    expandId,
    ...rest
  } = ownProps;
  const firstBetId = betsData?.[0]?.betId;
  const anchorId = firstBetId ? `match-group-${matchId}-${firstBetId}` : undefined;

  const { filterId } = useMatchBetFiltersContext();
  const location = useLocation();

  const selector = useMemo(() => makeBetGroupFactoryPropsSelector(), []);
  const selectorProps = useMemo(() => ({ matchId, betsData, filterId }), [betsData, filterId, matchId]);
  const { marketId, isMultimatch, ...selectedProps } = useSelector((state) =>
    selector(state, { matchId, betsData, filterId }),
  );

  const matchViewMode = useSelector((state) => selectMatchViewMode(state, selectorProps));
  const otherProps = useMemo(
    () => ({ matchId, locationHash: location.hash, locationPathname: location.pathname }),
    [location.hash, location.pathname, matchId],
  );
  const enhancedProps = useMergeProps({ selectedProps, otherProps });

  const [showOnlyStarters, setShowOnlyStarters] = useState(true);
  const playerIdFromFilterId = getPlayerIdFromFilterId(filterId);
  const displayBetSlidersByDefault = useSelector(selectUserDisplayBetSliders);
  const [selectedPresentationMode, setSelectedPresentationMode] = useState(
    getDefaultPresentationModeIndex({
      displayBetSlidersByDefault,
      betPresentationModes: enhancedProps.betPresentationModes,
    }),
  );
  const betPresentationMode = useMemo(
    () => enhancedProps.betPresentationModes?.[selectedPresentationMode] ?? enhancedProps.betPresentationModes[0],
    [enhancedProps.betPresentationModes, selectedPresentationMode],
  );

  const onStarterFilterClick = useCallback((onlyStarters) => {
    setShowOnlyStarters(onlyStarters);
  }, []);

  const myMatchViewModeIsAvailable = useMemo(
    () => matchViewMode === MatchViewModes.MY_MATCH && !!matchIdFromMatchView,
    [matchIdFromMatchView, matchViewMode],
  );

  const shouldDisplayMyMatchTooltip = useMemo(
    () => !!MyMatchHelperTooltipLabelsByMarketId[marketId] && !isMultimatch,
    [marketId, isMultimatch],
  );

  if (enhancedProps.groups.length) {
    return (
      <Wrapper className={className} $noPadding={showAlternativeTitle}>
        <MatchGroupHeader
          title={title}
          helperText={helperText}
          betPresentationModes={enhancedProps.betPresentationModes}
          betPresentationMode={betPresentationMode}
          selectedPresentationMode={selectedPresentationMode}
          setBetPresentationMode={setSelectedPresentationMode}
          showNewTag={enhancedProps.showNewTag}
          betId={firstBetId}
        />

        <MatchGroupBody $myMatchViewModeIsAvailable={myMatchViewModeIsAvailable}>
          {shouldDisplayMyMatchTooltip && matchViewMode !== MatchViewModes.MY_MATCH ? (
            <StyledInfosLabel
              symbol={'i'}
              type={InfosLabelTypes.INFOS_BLUE}
              label={MyMatchHelperTooltipLabelsByMarketId[marketId]()}
            />
          ) : null}
          <StyledMyMatchDynamicBackground isActive={myMatchViewModeIsAvailable} />

          {enhancedProps.showStarterFilter && !playerIdFromFilterId ? (
            <MatchGroupStarterFilters showOnlyStarters={showOnlyStarters} onStarterFilterClick={onStarterFilterClick} />
          ) : null}
          {enhancedProps.groups.map((group, index) => (
            <BetGroupFactory
              key={`group-idx-${index}`}
              expandId={`${expandId}-idx-${index}`}
              {...enhancedProps}
              {...group}
              {...rest}
              showAlternativeTitle={showAlternativeTitle}
              betPresentationMode={betPresentationMode}
              showOnlyStarters={enhancedProps.showStarterFilter && showOnlyStarters}
              headers={!index ? enhancedProps.headers : null}
              hasStarters={enhancedProps.showStarterFilter}
            />
          ))}
        </MatchGroupBody>
        {anchorId ? <AutoScrollAnchor data-anchorid={anchorId} /> : null}
      </Wrapper>
    );
  }

  return null;
};

export default BetGroupFactoryContainer;
